import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useWorkspace } from '@/lib/contexts/useWorkspace';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { AppHeader } from '@/components/layout/AppHeader';
import { getErrorMessage } from '@/lib/utils';
import toast from 'react-hot-toast';

export default function CreateWorkspacePage() {
  const navigate = useNavigate();
  const { createWorkspace } = useWorkspace();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Workspace name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await createWorkspace({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
      });
      
      toast.success('Workspace created successfully!');
      navigate('/app');
    } catch (error: unknown) {
      console.error('Failed to create workspace:', error);
      toast.error(getErrorMessage(error) || 'Failed to create workspace');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header - full width at top */}
      <AppHeader />
      
      {/* Content area with sidebar */}
      <div className="flex flex-1">
        <AppSidebar />
        
        {/* Main content */}
        <main className="flex-1 py-8">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Create New Workspace</h1>
              <p className="mt-2 text-gray-600">
                Set up a new workspace to organize your projects and collaborate with your team
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Workspace Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Workspace Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter workspace name"
                    maxLength={100}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Choose a clear name that describes your workspace purpose
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={4}
                    value={formData.description}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Describe the purpose of this workspace (optional)"
                    maxLength={500}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Help members understand what this workspace is for
                  </p>
                </div>

                {/* Workspace Features Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-blue-900 mb-2">What you'll get:</h3>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Your own dedicated workspace environment</li>
                    <li>• Project management and collaboration tools</li>
                    <li>• Member invitation and role management</li>
                    <li>• Secure data isolation from other workspaces</li>
                  </ul>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
                  <Link
                    to="/app"
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.name.trim()}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                        Creating...
                      </>
                    ) : (
                      'Create Workspace'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
